import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import type { GeoJsonGeometry } from '../../lib/geo.ts'
import { greenOutline } from '../../data/greenOutline.ts'
import { drawLook, genericShape, shapePath, type LookFrame, type LookShape } from '../../data/lookGeometry.ts'
import { LookMarks, useClipId } from './LookPicture.tsx'

/** the room left under the outline for the arrow of the play arriving */
const ARROW_ROOM = 9

/** M25-P (docs/M25.md C, E; rules 5 and 6): AN AREA'S REAL OUTLINE AS A PICTURE — the shape in force turned so that its line of play points up (the
 *  play arrives at the bottom, 6; the back is at the top, 12), holes kept, with the job's picture laid on it (the stripes clipped to it by the SVG).
 *  The line of play turns the drawing and nothing else. Where it is unknown (no bearing, or a shape this cannot read) the generic drawing is shown with
 *  the sentence « Sens de jeu inconnu pour ce green » (rule 6: never guessed, never forced); `showUnknown={false}` lets a page say it in its own words.
 *  `playArrow` draws, under the outline, the play arriving from 6. 56 px by default; stroke and fill in currentColor only; aria-hidden (the page's
 *  words carry the meaning). `data-outline` says which drawing it is: `real` or `generic`. */
export function OutlineSchema({ geom, bearing, code, frame, playArrow = false, showUnknown = true, className, testId }: {
  geom: GeoJsonGeometry | null | undefined; bearing: number | null; code: string | null; frame: LookFrame; playArrow?: boolean; showUnknown?: boolean; className?: string; testId?: string
}) {
  const { t } = useTranslation()
  const fit = useMemo(() => (bearing === null ? null : greenOutline(geom, bearing, { bottom: playArrow ? ARROW_ROOM : 0 })), [geom, bearing, playArrow])
  const shape: LookShape = useMemo(() => fit ?? genericShape(frame, 48), [fit, frame])
  const drawing = useMemo(() => (code ? drawLook(code, shape, 48) : null), [code, shape])
  const clipId = useClipId()
  const svg = (
    <svg viewBox="0 0 48 48" fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" focusable="false"
      className={className ?? 'size-14 shrink-0'} data-outline={fit ? 'real' : 'generic'} data-look={drawing ? drawing.code : undefined} data-testid={fit || !showUnknown ? testId : undefined}>
      {drawing ? <LookMarks drawing={drawing} clipId={clipId} /> : <path d={shapePath(shape)} fill="none" stroke="currentColor" strokeWidth={1.9} data-mark="outline" />}
      {fit && playArrow && <path d={`M24 46.5L24 ${48 - ARROW_ROOM + 1.5}M21 ${48 - ARROW_ROOM + 4.5}L24 ${48 - ARROW_ROOM + 1.5}L27 ${48 - ARROW_ROOM + 4.5}`} fill="none" stroke="currentColor" strokeWidth={1.6} data-mark="play" />}
    </svg>
  )
  if (fit || !showUnknown) return svg
  return (
    <span className="inline-flex flex-col items-start gap-1" data-testid={testId}>
      {svg}
      <span className="meta">{t('looks.outline.unknown')}</span>
    </span>
  )
}
